/**
 * The i18n runtime: a typed `t()`, a provider, and a language switch.
 *
 * WHY NOT react-i18next. The one property this app needs from an i18n layer is
 * that an untranslated key cannot ship. Twelve components are being translated
 * in parallel against these conventions, and with i18next a key missing from
 * `zh` silently falls back to English — the page looks translated in review and
 * is half English in production. Here `zh.ts` is a `Record<LocaleKey, string>`
 * over the flat key union of `en.ts`, so the same mistake is a failed
 * `npm run build`. That, plus free key autocomplete, plus no new dependency to
 * reconcile with this stack's TypeScript 6 / React 19 / Vite 8, is worth more
 * than the ~50 lines of runtime below. What i18next would have bought us —
 * ICU plurals for languages with six forms, lazy-loaded namespaces, a
 * translation-management pipeline — buys nothing for two languages, one of
 * which has no plural inflection at all.
 *
 * HOW TO USE IT, from a component:
 *
 *     import { useI18n } from '../i18n'
 *
 *     const { t } = useI18n()
 *     <h1>{t('auth.login.title')}</h1>
 *     <p>{t('common.requestCount', { count: n })}</p>
 *
 * The key convention — namespacing, `{{interpolation}}`, `_one`/`_other`
 * plurals — is documented at the top of `en.ts`. Read that file, not this one.
 *
 * Deliberate: `useI18n()` does NOT throw when there is no provider above it.
 * It falls back to English. Every existing component test mounts its subject
 * directly, and making the hook throw would turn a strings change into 262
 * broken tests; instead an untranslated-looking test is simply an English one.
 * The provider is wired in `providers.tsx`, which `App.test.tsx` mounts for
 * real, so a genuinely missing provider is still caught in one place.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { en } from './en'
import type { LocaleKey } from './en'
import { zh } from './zh'

export const LANGS = ['en', 'zh'] as const
export type Lang = (typeof LANGS)[number]

/** What goes in `<html lang>`: screen-reader pronunciation and font fallback. */
const HTML_LANG: Record<Lang, string> = { en: 'en', zh: 'zh-CN' }

const DICTS: Record<Lang, Record<string, string>> = { en, zh }

/**
 * Every key `t()` accepts: the literal keys of `en.ts`, plus the bare stem of
 * each `_one`/`_other` pair, because plural keys are called without the suffix.
 */
type PluralStem<K extends string> = K extends `${infer B}_one`
  ? B
  : K extends `${infer B}_other`
    ? B
    : never
export type TranslationKey = LocaleKey | PluralStem<LocaleKey>

/** Interpolation values are inserted verbatim — format numbers before passing. */
export type TParams = Record<string, string | number>

export type TFunction = (key: TranslationKey, params?: TParams) => string

export interface I18nValue {
  lang: Lang
  setLang: (lang: Lang) => void
  t: TFunction
}

const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/g

function interpolate(template: string, params?: TParams): string {
  if (params === undefined) return template
  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = params[name]
    // An unknown placeholder is left as-is: a typo should be visible in the
    // UI, not silently swallowed into an empty string.
    return value === undefined ? match : String(value)
  })
}

/**
 * `_one` at exactly one, `_other` everywhere else.
 *
 * `count` may arrive already formatted -- `'1,250'` -- because this app formats
 * its own numbers and the alternative is a sentence that disagrees with the
 * table beside it. The digits are what pick the form, so both call styles work
 * and a formatted count still reads as plural. Anything unparseable takes
 * `_other`, which is the safe form in English and the only form in Chinese.
 */
function pluralSuffix(count: string | number): 'one' | 'other' {
  const n = typeof count === 'number' ? count : Number(count.replace(/[^0-9.-]/g, ''))
  return n === 1 ? 'one' : 'other'
}

/** Plural selection, then exact key. */
function lookup(dict: Record<string, string>, key: string, params?: TParams): string | undefined {
  const count = params?.count
  if (count !== undefined) {
    const plural = dict[`${key}_${pluralSuffix(count)}`]
    if (plural !== undefined) return plural
  }
  return dict[key]
}

/**
 * Translate one key. Pure — no React, no globals — so tests and chart option
 * builders can call it directly with an explicit language.
 *
 * An unrecognised key renders as itself rather than throwing or blanking the
 * screen: a missing string is a typo to fix, never an outage on a monitoring
 * dashboard someone is using to look at an outage.
 */
export function translate(lang: Lang, key: TranslationKey, params?: TParams): string {
  const hit =
    lookup(DICTS[lang], key, params) ??
    (lang === 'en' ? undefined : lookup(DICTS.en, key, params))
  if (hit === undefined) {
    if (import.meta.env.DEV) console.warn(`i18n: missing key "${key}" for "${lang}"`)
    return key
  }
  return interpolate(hit, params)
}

export const LANG_STORAGE_KEY = 'tikhub.lang'

function isLang(value: unknown): value is Lang {
  return value === 'en' || value === 'zh'
}

/** The stored choice, or null. Wrapped: storage throws outright in some
 *  privacy modes, and a language preference is not worth a white screen. */
export function readStoredLang(): Lang | null {
  try {
    const raw = localStorage.getItem(LANG_STORAGE_KEY)
    return isLang(raw) ? raw : null
  } catch {
    return null
  }
}

function storeLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang)
  } catch {
    // Private mode. The choice holds for this tab and is forgotten on reload.
  }
}

/**
 * First visit: the browser's HIGHEST-RANKED language that we ship.
 *
 * `navigator.languages` is an ordered preference list, so the answer is the
 * first entry we recognise, not any entry we recognise. Scanning the whole list
 * for a zh tag put an operator whose list is ['en-US', 'en', 'zh-CN'] — someone
 * who reads English and has a Chinese keyboard installed — into a Chinese UI on
 * first load, with the switcher behind a sign-in form they could not read. A
 * tag we do not ship (fr) is skipped rather than ending the walk: it is not a
 * preference we can honour, and the next one may be.
 */
export function detectLang(): Lang {
  try {
    const tags = navigator.languages?.length ? navigator.languages : [navigator.language]
    for (const tag of tags) {
      const lower = tag?.toLowerCase() ?? ''
      if (lower.startsWith('zh')) return 'zh'
      if (lower.startsWith('en')) return 'en'
    }
    return 'en'
  } catch {
    return 'en'
  }
}

/** Only an explicit choice is persisted, so a detected default keeps tracking
 *  the browser rather than freezing on whatever the first visit guessed. */
export function initialLangFor(explicit?: Lang): Lang {
  return explicit ?? readStoredLang() ?? detectLang()
}

const FALLBACK: I18nValue = {
  lang: 'en',
  setLang: () => {},
  t: (key, params) => translate('en', key, params),
}

const I18nContext = createContext<I18nValue>(FALLBACK)

/** The active language, for the few call sites that are not components
 *  (thrown error messages, module-level formatters). Not reactive: a string
 *  read through this will not re-render on a language switch, so prefer
 *  storing a `TranslationKey` in the data and calling `t()` at render time. */
let activeLang: Lang = 'en'

export function translateStatic(key: TranslationKey, params?: TParams): string {
  return translate(activeLang, key, params)
}

/**
 * `initialLang` is for tests and stories; the app passes nothing and lets the
 * stored choice, then `navigator.language`, decide. It is read once — changing
 * the prop later does not move the language, `setLang` does.
 */
export function I18nProvider({
  children,
  initialLang,
}: {
  children: ReactNode
  initialLang?: Lang
}) {
  const [lang, setLangState] = useState<Lang>(() => initialLangFor(initialLang))

  const setLang = useCallback((next: Lang) => {
    setLangState(next)
    storeLang(next)
  }, [])

  useEffect(() => {
    activeLang = lang
    document.documentElement.lang = HTML_LANG[lang]
    // The tab title is chrome too. `index.html` ships a static English one
    // because the document exists before React does; from here on it follows
    // the switch like everything else, so a bookmark taken in a Chinese
    // session is named in Chinese.
    document.title = translate(lang, 'nav.document.title')
  }, [lang])

  const value = useMemo<I18nValue>(
    () => ({ lang, setLang, t: (key, params) => translate(lang, key, params) }),
    [lang, setLang],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

/** `const { t } = useI18n()`. English when no provider is mounted above. */
export function useI18n(): I18nValue {
  return useContext(I18nContext)
}
